import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';

// Helper functions defined outside component to prevent re-creation
const countWords = (text) => {
  if (!text) return 0;
  return text.trim().split(/\s+/).filter(w => w.length > 0).length;
};

const validateFieldWithLimits = (fieldName, value, limits) => {
  const fieldLimit = limits[fieldName];
  if (!fieldLimit) return { valid: true, status: 'good' };

  const wordCount = countWords(value);
  let status = 'good';

  if (wordCount === 0) {
    status = 'empty';
  } else if (wordCount < fieldLimit.min) {
    status = 'short';
  } else if (wordCount > fieldLimit.max) {
    status = 'long';
  } else if (wordCount > fieldLimit.max - 5) {
    status = 'warning';
  }

  return {
    valid: wordCount === 0 || (wordCount >= fieldLimit.min && wordCount <= fieldLimit.max),
    wordCount,
    min: fieldLimit.min,
    max: fieldLimit.max,
    target: fieldLimit.target,
    status
  };
};

const getStatusColor = (status) => {
  switch (status) {
    case 'good': return '#28a745';
    case 'warning': return '#ffc107';
    case 'short': return '#17a2b8';
    case 'long': return '#dc3545';
    case 'empty': return '#6c757d';
    default: return '#6c757d';
  }
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'good': return '✓';
    case 'warning': return '⚠';
    case 'short': return '↓';
    case 'long': return '✗';
    default: return '';
  }
};

// TextFieldWithCounter defined outside main component to prevent re-creation on every render
const TextFieldWithCounter = ({ label, fieldName, rows = 3, placeholder, value, limits, validation, onChange }) => {
  const val = validation || {};

  return (
    <div className="mb-3">
      <label className="form-label fw-bold">
        {label}
        {limits && (
          <span className="ms-2 fw-normal text-muted" style={{ fontSize: '0.85rem' }}>
            (Target: {limits.target})
          </span>
        )}
      </label>
      <textarea
        className="form-control"
        rows={rows}
        value={value || ''}
        onChange={(e) => onChange(fieldName, e.target.value)}
        placeholder={placeholder}
        style={{
          borderColor: val.status === 'long' ? '#dc3545' : undefined
        }}
      />
      {limits && (
        <div className="d-flex justify-content-between mt-1" style={{ fontSize: '0.85rem' }}>
          <span style={{ color: getStatusColor(val.status) }}>
            {getStatusIcon(val.status)} {val.wordCount || 0} / {limits.max} words
          </span>
          {val.status === 'short' && <span className="text-info">Need {limits.min - (val.wordCount || 0)} more words</span>}
          {val.status === 'long' && <span className="text-danger">{(val.wordCount || 0) - limits.max} words over limit</span>}
        </div>
      )}
    </div>
  );
};

const FundContentEditor = () => {
  const [funds, setFunds] = useState([]);
  const [selectedFund, setSelectedFund] = useState(null);
  const [formData, setFormData] = useState({});
  const [fieldLimits, setFieldLimits] = useState({});
  const [validation, setValidation] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'with_content', 'without_content'
  const [searchTerm, setSearchTerm] = useState('');
  const [overviewCount, setOverviewCount] = useState(3);
  const [opinionCount, setOpinionCount] = useState(4);
  const [portfolioCount, setPortfolioCount] = useState(4);

  // Word budgets for warnings
  const OPINION_BUDGET = 180;
  const PORTFOLIO_BUDGET = 200;

  // Fetch available funds on mount
  useEffect(() => {
    fetchFunds();
  }, []);

  // Refetch field limits when counts change
  useEffect(() => {
    fetchFieldLimits();
  }, [opinionCount, portfolioCount]);

  const fetchFunds = async () => {
    try {
      const response = await api.get('/api/admin/fund-content/available-funds');
      setFunds(response.data);
    } catch (err) {
      console.error('Error fetching funds:', err);
      setMessage({ type: 'error', text: 'Failed to load funds' });
    }
  };

  const fetchFieldLimits = async () => {
    try {
      const response = await api.get('/api/admin/fund-content/field-limits', {
        params: { opinion_count: opinionCount, portfolio_count: portfolioCount }
      });
      setFieldLimits(response.data);
    } catch (err) {
      console.error('Error fetching field limits:', err);
    }
  };

  // Calculate section word totals
  const getSectionWordTotal = (prefix, count) => {
    let total = 0;
    for (let i = 1; i <= count; i++) {
      total += countWords(formData[`${prefix}_${i}`] || '');
    }
    return total;
  };

  const loadFundContent = async (apir) => {
    setIsLoading(true);
    try {
      const response = await api.get(`/api/admin/fund-content/${apir}`);
      setFormData(response.data);

      // Auto-detect how many fields have content (minimum 1)
      let overviewFields = 1;
      for (let i = 3; i >= 1; i--) {
        if (response.data[`overview_paragraph_${i}`]?.trim()) {
          overviewFields = i;
          break;
        }
      }
      setOverviewCount(overviewFields);

      let opinionFields = 1;
      for (let i = 6; i >= 1; i--) {
        if (response.data[`opinion_${i}`]?.trim()) {
          opinionFields = i;
          break;
        }
      }
      setOpinionCount(opinionFields);

      let portfolioFields = 1;
      for (let i = 6; i >= 1; i--) {
        if (response.data[`portfolio_${i}`]?.trim()) {
          portfolioFields = i;
          break;
        }
      }
      setPortfolioCount(portfolioFields);

      validateAllFields(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        // No content yet - start with empty form
        const fund = funds.find(f => f.apir === apir);
        const emptyForm = {
          apir_code: apir,
          fund_name: fund?.name || '',
          overview_paragraph_1: '',
          overview_paragraph_2: '',
          overview_paragraph_3: '',
          opinion_1: '',
          opinion_2: '',
          opinion_3: '',
          opinion_4: '',
          opinion_5: '',
          opinion_6: '',
          portfolio_1: '',
          portfolio_2: '',
          portfolio_3: '',
          portfolio_4: '',
          portfolio_5: '',
          portfolio_6: '',
          fund_summary: '',
        };
        setFormData(emptyForm);
        setOverviewCount(3);
        setOpinionCount(4);
        setPortfolioCount(4);
        validateAllFields(emptyForm);
      } else {
        setMessage({ type: 'error', text: 'Failed to load fund content' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const validateAllFields = useCallback((data) => {
    const newValidation = {};
    Object.keys(fieldLimits).forEach(field => {
      newValidation[field] = validateFieldWithLimits(field, data[field] || '', fieldLimits);
    });
    setValidation(newValidation);
  }, [fieldLimits]);

  const handleFieldChange = useCallback((fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));

    // Validate this field
    setValidation(prev => ({
      ...prev,
      [fieldName]: validateFieldWithLimits(fieldName, value, fieldLimits)
    }));
  }, [fieldLimits]);

  const handleSelectFund = (fund) => {
    setSelectedFund(fund);
    loadFundContent(fund.apir);
    setMessage(null);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setMessage(null);

    try {
      const fund = funds.find(f => f.apir === formData.apir_code);
      const isNew = !fund?.has_content;

      if (isNew) {
        await api.post('/api/admin/fund-content', formData);
      } else {
        await api.put(`/api/admin/fund-content/${formData.apir_code}`, formData);
      }

      setMessage({ type: 'success', text: 'Fund content saved successfully!' });
      fetchFunds(); // Refresh the list
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to save' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this fund content?')) return;

    try {
      await api.delete(`/api/admin/fund-content/${formData.apir_code}`);
      setMessage({ type: 'success', text: 'Fund content deleted' });
      setSelectedFund(null);
      setFormData({});
      fetchFunds();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to delete' });
    }
  };

  // Filter and search funds
  const filteredFunds = funds.filter(fund => {
    const matchesFilter = filter === 'all' ||
      (filter === 'with_content' && fund.has_content) ||
      (filter === 'without_content' && !fund.has_content);

    const matchesSearch = !searchTerm ||
      fund.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      fund.apir.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesFilter && matchesSearch;
  });

  // Group by asset class
  const groupedFunds = filteredFunds.reduce((acc, fund) => {
    if (!acc[fund.asset_class]) acc[fund.asset_class] = [];
    acc[fund.asset_class].push(fund);
    return acc;
  }, {});

  return (
    <div className="fund-content-editor">
      <h4 className="mb-4">Fund Content Editor</h4>

      {message && (
        <div className={`alert alert-${message.type === 'error' ? 'danger' : 'success'} alert-dismissible`}>
          {message.text}
          <button type="button" className="btn-close" onClick={() => setMessage(null)}></button>
        </div>
      )}

      <div className="row">
        {/* Left Panel - Fund List */}
        <div className="col-md-4">
          <div className="card">
            <div className="card-header">
              <h6 className="mb-0">Select Fund</h6>
            </div>
            <div className="card-body p-2">
              {/* Search and Filter */}
              <input
                type="text"
                className="form-control form-control-sm mb-2"
                placeholder="Search funds..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <div className="btn-group btn-group-sm w-100 mb-2">
                <button
                  className={`btn ${filter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setFilter('all')}
                >
                  All
                </button>
                <button
                  className={`btn ${filter === 'with_content' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setFilter('with_content')}
                >
                  With Content
                </button>
                <button
                  className={`btn ${filter === 'without_content' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setFilter('without_content')}
                >
                  No Content
                </button>
              </div>

              {/* Fund List */}
              <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                {Object.entries(groupedFunds).map(([assetClass, classFunds]) => (
                  <div key={assetClass} className="mb-2">
                    <div className="fw-bold text-muted small mb-1">{assetClass}</div>
                    {classFunds.map(fund => (
                      <div
                        key={fund.apir}
                        className={`p-2 border rounded mb-1 cursor-pointer ${selectedFund?.apir === fund.apir ? 'bg-primary text-white' : ''}`}
                        style={{ cursor: 'pointer', fontSize: '0.85rem' }}
                        onClick={() => handleSelectFund(fund)}
                      >
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="text-truncate" style={{ maxWidth: '200px' }}>{fund.name}</span>
                          {fund.has_content ? (
                            <span className="badge bg-success">✓</span>
                          ) : (
                            <span className="badge bg-secondary">-</span>
                          )}
                        </div>
                        <small className={selectedFund?.apir === fund.apir ? 'text-white-50' : 'text-muted'}>
                          {fund.apir}
                        </small>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel - Edit Form */}
        <div className="col-md-8">
          {isLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border"></div>
              <p className="mt-2">Loading...</p>
            </div>
          ) : selectedFund ? (
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="mb-0">{formData.fund_name}</h6>
                  <small className="text-muted">{formData.apir_code}</small>
                </div>
                <div>
                  <button
                    className="btn btn-primary btn-sm me-2"
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                  {selectedFund.has_content && (
                    <button className="btn btn-outline-danger btn-sm" onClick={handleDelete}>
                      Delete
                    </button>
                  )}
                </div>
              </div>
              <div className="card-body" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                {/* Fund Name Display Section */}
                <h6 className="border-bottom pb-2 mb-3" style={{ color: '#072040' }}>FUND NAME</h6>
                <div className="mb-4">
                  <div className="row">
                    <div className="col-md-6">
                      <label className="form-label fw-bold">Original Name</label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={formData.original_name || formData.fund_name || ''}
                        disabled
                        style={{ color: '#6c757d' }}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-bold">
                        Display Name
                        {formData.name_override && (
                          <span className="badge bg-info text-white ms-2">Override Active</span>
                        )}
                      </label>
                      <input
                        type="text"
                        className="form-control bg-light"
                        value={formData.name_override || formData.original_name || formData.fund_name || ''}
                        disabled
                        style={{ color: formData.name_override ? '#072040' : '#6c757d', fontWeight: formData.name_override ? '500' : 'normal' }}
                      />
                      <small className="text-muted">
                        {formData.name_override
                          ? 'Name override set in source data file'
                          : 'To override, add name_override column to source Excel file'}
                      </small>
                    </div>
                  </div>
                </div>

                {/* Overview Section */}
                <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-3">
                  <h6 className="mb-0" style={{ color: '#072040' }}>
                    OVERVIEW
                    <span className="badge bg-secondary ms-2">{overviewCount} paragraph{overviewCount !== 1 ? 's' : ''}</span>
                  </h6>
                  <div className="btn-group btn-group-sm">
                    {overviewCount < 3 && (
                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={() => setOverviewCount(c => c + 1)}
                      >
                        + Add Paragraph
                      </button>
                    )}
                    {overviewCount > 1 && (
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => {
                          setFormData(prev => ({ ...prev, [`overview_paragraph_${overviewCount}`]: '' }));
                          setOverviewCount(c => c - 1);
                        }}
                      >
                        − Remove
                      </button>
                    )}
                  </div>
                </div>
                {[1, 2, 3].slice(0, overviewCount).map(i => (
                  <TextFieldWithCounter
                    key={`overview_paragraph_${i}`}
                    label={`Overview Paragraph ${i}`}
                    fieldName={`overview_paragraph_${i}`}
                    rows={4}
                    placeholder={i === 1 ? "Describe the fund manager, history, and team structure..." :
                                i === 2 ? "Describe the investment strategy and approach..." :
                                "Describe performance track record and objectives..."}
                    value={formData[`overview_paragraph_${i}`]}
                    limits={fieldLimits[`overview_paragraph_${i}`]}
                    validation={validation[`overview_paragraph_${i}`]}
                    onChange={handleFieldChange}
                  />
                ))}

                {/* Drummond's Opinion */}
                <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-3 mt-4">
                  <h6 className="mb-0" style={{ color: '#072040' }}>
                    DRUMMOND'S OPINION
                    <span className="badge bg-secondary ms-2">{opinionCount} points</span>
                  </h6>
                  <div className="btn-group btn-group-sm">
                    {opinionCount < 6 && (
                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={() => setOpinionCount(c => c + 1)}
                      >
                        + Add Point
                      </button>
                    )}
                    {opinionCount > 1 && (
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => {
                          setFormData(prev => ({ ...prev, [`opinion_${opinionCount}`]: '' }));
                          setOpinionCount(c => c - 1);
                        }}
                      >
                        − Remove
                      </button>
                    )}
                  </div>
                </div>
                {(() => {
                  const opinionTotal = getSectionWordTotal('opinion', opinionCount);
                  return opinionTotal > OPINION_BUDGET && (
                    <div className="alert alert-warning py-2 mb-3">
                      <small>⚠️ Section total ({opinionTotal} words) exceeds {OPINION_BUDGET} word budget. PDF may require font scaling.</small>
                    </div>
                  );
                })()}
                {[1, 2, 3, 4, 5, 6].slice(0, opinionCount).map(i => (
                  <TextFieldWithCounter
                    key={`opinion_${i}`}
                    label={`Opinion Point ${i}`}
                    fieldName={`opinion_${i}`}
                    rows={3}
                    value={formData[`opinion_${i}`]}
                    limits={fieldLimits[`opinion_${i}`]}
                    validation={validation[`opinion_${i}`]}
                    onChange={handleFieldChange}
                  />
                ))}

                {/* Portfolio Construction */}
                <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-3 mt-4">
                  <h6 className="mb-0" style={{ color: '#072040' }}>
                    PORTFOLIO CONSTRUCTION CONSIDERATIONS
                    <span className="badge bg-secondary ms-2">{portfolioCount} points</span>
                  </h6>
                  <div className="btn-group btn-group-sm">
                    {portfolioCount < 6 && (
                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={() => setPortfolioCount(c => c + 1)}
                      >
                        + Add Point
                      </button>
                    )}
                    {portfolioCount > 1 && (
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => {
                          setFormData(prev => ({ ...prev, [`portfolio_${portfolioCount}`]: '' }));
                          setPortfolioCount(c => c - 1);
                        }}
                      >
                        − Remove
                      </button>
                    )}
                  </div>
                </div>
                {(() => {
                  const portfolioTotal = getSectionWordTotal('portfolio', portfolioCount);
                  return portfolioTotal > PORTFOLIO_BUDGET && (
                    <div className="alert alert-warning py-2 mb-3">
                      <small>⚠️ Section total ({portfolioTotal} words) exceeds {PORTFOLIO_BUDGET} word budget. PDF may require font scaling.</small>
                    </div>
                  );
                })()}
                {[1, 2, 3, 4, 5, 6].slice(0, portfolioCount).map(i => (
                  <TextFieldWithCounter
                    key={`portfolio_${i}`}
                    label={`Consideration ${i}`}
                    fieldName={`portfolio_${i}`}
                    rows={3}
                    value={formData[`portfolio_${i}`]}
                    limits={fieldLimits[`portfolio_${i}`]}
                    validation={validation[`portfolio_${i}`]}
                    onChange={handleFieldChange}
                  />
                ))}

                {/* Fund Summary */}
                <h6 className="border-bottom pb-2 mb-3 mt-4" style={{ color: '#072040' }}>FUND SUMMARY (Sidebar)</h6>
                <TextFieldWithCounter
                  label="Fund Summary"
                  fieldName="fund_summary"
                  rows={2}
                  placeholder="Brief summary for the sidebar box..."
                  value={formData.fund_summary}
                  limits={fieldLimits.fund_summary}
                  validation={validation.fund_summary}
                  onChange={handleFieldChange}
                />
              </div>
            </div>
          ) : (
            <div className="text-center py-5 text-muted">
              <p>Select a fund from the list to edit its content</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FundContentEditor;
